import { useState, useEffect } from "react";
import { api } from "../App";
import { Calendar, Plus, X } from "lucide-react";
import { toast } from "sonner";

const SHIFT_COLORS = { AM: "#F59E0B", PM: "#06B6D4", FULL_DAY: "#A855F7" };

export default function ProductionShoots() {
  const [shoots, setShoots] = useState([]);
  const [studios, setStudios] = useState([]);
  const [productions, setProductions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ production_id: "", studio_id: "", title: "", start_at: "", end_at: "", shift: "FULL_DAY", venue_type: "studio", director_id: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { loadData(); }, []);
  const loadData = async () => {
    try {
      const [s, st, p] = await Promise.all([api.get("/shoots"), api.get("/studios"), api.get("/productions?status=active")]);
      setShoots(s.data); setStudios(st.data); setProductions(p.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.production_id || !form.title || !form.start_at || !form.end_at) { toast.error("Fill required fields"); return; }
    setSubmitting(true);
    try {
      await api.post("/shoots", form);
      toast.success("Shoot scheduled!");
      setShowAdd(false);
      setForm({ production_id: "", studio_id: "", title: "", start_at: "", end_at: "", shift: "FULL_DAY", venue_type: "studio", director_id: "" });
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed — possible scheduling conflict"); }
    finally { setSubmitting(false); }
  };

  // Group by date
  const grouped = {};
  shoots.forEach(s => { const d = s.date || "Unknown"; grouped[d] = grouped[d] || []; grouped[d].push(s); });
  const sortedDates = Object.keys(grouped).sort();

  if (loading) return <><div className="flex items-center justify-center h-64"><div className="animate-pulse text-amber-400 font-heading text-xl">LOADING...</div></div></>;

  return (
    <>
      <div data-testid="production-shoots" className="space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-2xl sm:text-3xl text-[var(--t1)] tracking-tight">SHOOTS</h1>
          <button data-testid="add-shoot-btn" onClick={() => setShowAdd(true)} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black text-sm font-bold rounded-full hover:bg-amber-400 transition-colors"><Plus size={16} /> Schedule</button>
        </div>
        {sortedDates.length === 0 ? (
          <div className="text-center py-16"><Calendar size={40} className="mx-auto text-white/10 mb-4" /><p className="text-[var(--t3)] text-sm">No shoots scheduled. Schedule your first shoot to get the machine rolling.</p></div>
        ) : (
          sortedDates.map(date => (
            <div key={date}>
              <p className="text-[var(--t3)] text-xs tracking-[0.2em] mb-2 font-heading">{new Date(date + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short" })}</p>
              <div className="space-y-2">
                {grouped[date].map(s => {
                  const studioName = studios.find(st => st.studio_id === s.studio_id)?.name || s.location_name || "TBD";
                  return (
                    <div key={s.shoot_id} className="glass-card rounded-xl p-4 candidate-editorial" data-testid={`shoot-${s.shoot_id}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-semibold text-sm normal-case">{s.title}</p>
                          <p className="text-white/25 text-xs normal-case">{studioName} | {s.start_at?.slice(11, 16)} - {s.end_at?.slice(11, 16)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[12px] px-2 py-0.5 rounded-full" style={{ backgroundColor: (SHIFT_COLORS[s.shift] || "#A855F7") + "15", color: SHIFT_COLORS[s.shift] || "#A855F7" }}>{s.shift}</span>
                          <span className={`text-[12px] px-2 py-0.5 rounded-full ${s.status === "confirmed" ? "bg-green-500/15 text-green-400" : s.status === "wrapped" ? "bg-cyan-500/15 text-cyan-400" : "bg-[var(--s2)] text-[var(--t3)]"}`}>{s.status}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
        {showAdd && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowAdd(false)} />
            <form onSubmit={handleAdd} className="relative glass-card rounded-2xl p-6 w-full max-w-md space-y-4 max-h-[85vh] overflow-y-auto">
              <div className="flex items-center justify-between"><h2 className="font-heading text-xl text-[var(--t1)]">SCHEDULE SHOOT</h2><button type="button" onClick={() => setShowAdd(false)}><X size={20} className="text-text-muted" /></button></div>
              <select value={form.production_id} onChange={(e) => setForm({...form, production_id: e.target.value})} className="form-input bg-transparent cursor-pointer" required>
                <option value="" className="bg-black">Select Production *</option>
                {productions.map(p => <option key={p.production_id} value={p.production_id} className="bg-black">{p.title}</option>)}
              </select>
              <input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Shoot Title *" className="form-input" required />
              <select value={form.venue_type} onChange={(e) => setForm({...form, venue_type: e.target.value})} className="form-input bg-transparent cursor-pointer">
                <option value="studio" className="bg-black">Studio</option>
                <option value="outdoor" className="bg-black">Outdoor</option>
                <option value="client_site" className="bg-black">Client Site</option>
              </select>
              {form.venue_type === "studio" && (
                <select value={form.studio_id} onChange={(e) => setForm({...form, studio_id: e.target.value})} className="form-input bg-transparent cursor-pointer">
                  <option value="" className="bg-black">Select Studio</option>
                  {studios.map(s => <option key={s.studio_id} value={s.studio_id} className="bg-black">{s.name}</option>)}
                </select>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="text-[var(--t3)] text-xs mb-1 block">Start *</label><input type="datetime-local" value={form.start_at} onChange={(e) => setForm({...form, start_at: e.target.value})} className="form-input" required /></div>
                <div><label className="text-[var(--t3)] text-xs mb-1 block">End *</label><input type="datetime-local" value={form.end_at} onChange={(e) => setForm({...form, end_at: e.target.value})} className="form-input" required /></div>
              </div>
              <select value={form.shift} onChange={(e) => setForm({...form, shift: e.target.value})} className="form-input bg-transparent cursor-pointer">
                <option value="AM" className="bg-black">AM (Morning)</option>
                <option value="PM" className="bg-black">PM (Afternoon)</option>
                <option value="FULL_DAY" className="bg-black">Full Day</option>
              </select>
              <button type="submit" disabled={submitting} className="w-full py-3 bg-amber-500 text-black font-bold rounded-xl hover:bg-amber-400 transition-colors disabled:opacity-50">{submitting ? "Scheduling..." : "Schedule Shoot"}</button>
            </form>
          </div>
        )}
      </div>
    </>
  );
}
